import { API_TYPES } from "@src/appV2/api";
import { type PlacementData } from "@src/appV2/redesign/Placements/types/fetchPlacements.schema";
import { type PlacementData as PlacementDisplayRepresentation } from "@src/appV2/redesign/Placements/types/placement";

import { type GetWorkerFavoritePlacementsResponse } from "../api/useGetWorkerFavoritePlacements";

export function constructPlacementDataFromWorkerFavoriteResponse(
  placement: PlacementData,
  favoritePlacementsData: GetWorkerFavoritePlacementsResponse,
  credentialRequirementConfiguration: Set<string>
): Omit<
  PlacementDisplayRepresentation,
  "score" | "scoreBreakdown" | "baseRate" | "totalRate" | "totalRateBreakdown"
> {
  const workplace = favoritePlacementsData.included
    .filter((included) => included.type === API_TYPES.workplace)
    .find((included) => included.id === placement.relationships.workplace.data.id);

  const interviewSettings = favoritePlacementsData.included
    .filter((included) => included.type === API_TYPES.interviewSettings)
    .find(
      (included) =>
        placement.relationships.interviewSettings?.data?.id === included.id &&
        included.attributes.scheduleInterviews
    );

  const allWorkerRequirements = favoritePlacementsData.included
    .filter((included) => included.type === API_TYPES.workerRequirement)
    .map((workerRequirement) => {
      const requirements = favoritePlacementsData.included
        .filter((included) => included.type === API_TYPES.requirement)
        .find((included) => included.id === workerRequirement.relationships.requirement?.data?.id);

      return {
        ...workerRequirement.attributes,
        id: workerRequirement.id,
        requirement: {
          ...requirements?.attributes,
          name: requirements?.attributes.name ?? "",
          id: workerRequirement.relationships.requirement.data.id,
        },
      };
    });

  const workerRequirements = allWorkerRequirements.filter(
    (workerRequirement) => !credentialRequirementConfiguration.has(workerRequirement.requirement.id)
  );

  const credentialRequirements = allWorkerRequirements.filter((workerRequirement) =>
    credentialRequirementConfiguration.has(workerRequirement.requirement.id)
  );

  const hiringEvents = favoritePlacementsData.included
    .filter((included) => included.type === API_TYPES.hiringEvent)
    .filter((included) =>
      placement.relationships.hiringEvents?.data?.some((he) => he.id === included.id)
    )
    .map((event) => {
      return {
        id: event.id,
        ...event.attributes,
      };
    });

  const workerFavoritePlacementId = favoritePlacementsData.data.find(
    (fav) => fav.relationships.placement.data.id === placement.id
  )?.id;

  return {
    ...placement.meta,
    ...placement.attributes,
    isFavorite: true,
    workerFavoritePlacementId,
    perks: placement.attributes.perks ?? [],
    transparencyPayRates: placement.attributes.transparencyPayRates ?? {
      baseRates: [{ baseRate: 0, minYearsOfExperience: 0 }],
    },
    allowRequestInterview: placement.meta.allowRequestInterview ?? false,
    workerTypes: placement.attributes.workerTypes ?? [],
    payRange: placement.attributes.payRange ?? {},
    distance: placement.attributes.distance,
    tags: placement.attributes.tags ?? [],
    id: placement.id,
    matchedType: "exact",
    hiringEvents,
    workerRequirements,
    credentialRequirements,
    interviews: [],
    applications: [],
    interviewSettings: {
      ...interviewSettings?.attributes,
      scheduleInterviews: interviewSettings?.attributes.scheduleInterviews ?? false,
      availability: interviewSettings?.attributes.availability ?? [],
      id: interviewSettings?.id ?? "",
    },
    workplace: {
      id: workplace?.id ?? "",
      name: workplace?.attributes.name ?? "",
      neighborhood: workplace?.attributes.address.neighborhood,
    },
  };
}
